import { cn } from '@/lib/utils';
import * as React from 'react';

const countryCodes = [
  { value: '+234', label: 'Nigeria', flag: '🇳🇬' },
  { value: '+254', label: 'Kenya', flag: '🇰🇪' },
  { value: '+1', label: 'USA', flag: '🇺🇸' },
  { value: '+44', label: 'UK', flag: '🇬🇧' },
  { value: '+91', label: 'India', flag: '🇮🇳' },
  { value: '+86', label: 'China', flag: '🇨🇳' },
  { value: '+61', label: 'Australia', flag: '🇦🇺' },
  { value: '+49', label: 'Germany', flag: '🇩🇪' },
  { value: '+33', label: 'France', flag: '🇫🇷' },
  { value: '+81', label: 'Japan', flag: '🇯🇵' },
  { value: '+7', label: 'Russia', flag: '🇷🇺' },
  { value: '+55', label: 'Brazil', flag: '🇧🇷' },
  { value: '+27', label: 'South Africa', flag: '🇿🇦' },
  { value: '+20', label: 'Egypt', flag: '🇪🇬' },
  { value: '+971', label: 'UAE', flag: '🇦🇪' },
  { value: '+256', label: 'Uganda', flag: '🇺🇬' },
  { value: '+255', label: 'Tanzania', flag: '🇹🇿' },
  { value: '+251', label: 'Ethiopia', flag: '🇪🇹' },
  { value: '+250', label: 'Rwanda', flag: '🇷🇼' },
  { value: '+260', label: 'Zambia', flag: '🇿🇲' },
  { value: '+263', label: 'Zimbabwe', flag: '🇿🇼' },
  { value: '+233', label: 'Ghana', flag: '🇬🇭' },
  { value: '+225', label: "Côte d'Ivoire", flag: '🇨🇮' },
  { value: '+237', label: 'Cameroon', flag: '🇨🇲' },
  { value: '+221', label: 'Senegal', flag: '🇸🇳' },
  { value: '+212', label: 'Morocco', flag: '🇲🇦' },
  { value: '+213', label: 'Algeria', flag: '🇩🇿' },
  { value: '+216', label: 'Tunisia', flag: '🇹🇳' },
  { value: '+218', label: 'Libya', flag: '🇱🇾' },
  { value: '+249', label: 'Sudan', flag: '🇸🇩' },
  { value: '+966', label: 'Saudi Arabia', flag: '🇸🇦' },
  { value: '+90', label: 'Turkey', flag: '🇹🇷' },
  { value: '+92', label: 'Pakistan', flag: '🇵🇰' },
  { value: '+880', label: 'Bangladesh', flag: '🇧🇩' },
  { value: '+84', label: 'Vietnam', flag: '🇻🇳' },
  { value: '+82', label: 'South Korea', flag: '🇰🇷' },
  { value: '+62', label: 'Indonesia', flag: '🇮🇩' },
  { value: '+60', label: 'Malaysia', flag: '🇲🇾' },
  { value: '+65', label: 'Singapore', flag: '🇸🇬' },
  { value: '+66', label: 'Thailand', flag: '🇹🇭' },
  { value: '+63', label: 'Philippines', flag: '🇵🇭' },
];

interface PhoneInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value'
  > {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  error?: string;
}

export function PhoneInput({
  value,
  onChange,
  className,
  error,
  ...props
}: PhoneInputProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const countryCodeMatch = value.match(/^\+\d+/) || ['+234'];
  const countryCode = countryCodeMatch[0];
  const phoneNumber = value.replace(countryCode, '').trim();

  const selectedCountry =
    countryCodes.find(c => c.value === countryCode) || countryCodes[0];

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value.replace(/[^0-9\s-]/g, '');
    onChange(`${countryCode} ${newNumber}`.trim());
  };

  const handleCountryCodeChange = (code: string) => {
    onChange(`${code} ${phoneNumber}`.trim());
    setIsOpen(false);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative w-full">
      <div
        className={cn(
          'flex items-center rounded-xl border border-gray-200 bg-white focus-within:ring-2 focus-within:ring-primary focus-within:outline-none transition-all duration-200 ease-out-cubic w-full',
          error ? 'border-red-500' : 'border-gray-200',
          className
        )}
        ref={containerRef}
      >
        <div
          className="flex items-center gap-1 px-2 py-2 rounded-l-xl cursor-pointer border-r border-gray-200 flex-shrink-0"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="text-sm">{selectedCountry.flag}</span>
          <span className="text-xs text-gray-600">{countryCode}</span>
        </div>
        <input
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          className="flex-1 min-w-0 px-2 py-2 rounded-r-xl text-xs md:text-sm text-gray-600 focus:outline-none bg-white placeholder-gray-400"
          placeholder="Enter phone number"
          {...props}
        />
      </div>
      {isOpen && (
        <div
          className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg animate-in fade-in-0 zoom-in-95 duration-200 ease-out-cubic"
          ref={dropdownRef}
          style={{ maxHeight: '200px' }}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 p-1. 5">
            <input
              type="text"
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md"
              placeholder="Search countries..."
              onClick={e => e.stopPropagation()}
            />
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: '160px' }}>
            {countryCodes.map(country => (
              <div
                key={country.value}
                className="flex items-center gap-1. 5 px-2 py-1.5 hover:bg-gray-100 cursor-pointer transition-colors duration-200 ease-out-cubic"
                onClick={() => handleCountryCodeChange(country.value)}
              >
                <span className="text-sm">{country.flag}</span>
                <span className="text-xs">{country.value}</span>
                <span className="text-gray-500 text-xs truncate">
                  {country.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {error && (
        <p className="text-xs text-red-600 mt-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
